import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { adminAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { 
  Users, 
  UserPlus, 
  UserMinus, 
  Shield, 
  Mail, 
  Calendar,
  BarChart3,
  Trash2,
  Plus,
  RefreshCw,
  MessageSquare
} from 'lucide-react';

interface User {
  email: string;
  added_by: string;
  added_at: string;
}

interface Stats {
  total_users: number;
  admin_users: number;
  regular_users: number;
  pending_requests: number;
}

export const AdminPanel: React.FC = () => {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [stats, setStats] = useState<Stats>({ total_users: 0, admin_users: 0, regular_users: 0, pending_requests: 0 });
  const [isLoading, setIsLoading] = useState(false);
  const [newUserEmail, setNewUserEmail] = useState('');
  const [isAddingUser, setIsAddingUser] = useState(false);

  useEffect(() => {
    fetchUsers();
    fetchStats();
  }, []);

  const fetchUsers = async () => {
    try {
      setIsLoading(true);
      const response = await adminAPI.getUsers();
      if (response.status === 'success') {
        setUsers(response.users);
      } else {
        toast.error('Failed to fetch users');
      }
    } catch (error: any) {
      console.error('Error fetching users:', error);
      if (error.response?.status === 403) {
        toast.error('Admin access required');
      } else {
        toast.error('Failed to fetch users');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await adminAPI.getStats();
      if (response.status === 'success') {
        setStats(response.stats);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleAddUser = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newUserEmail.trim()) {
      toast.error('Please enter an email address');
      return;
    }

    try {
      setIsAddingUser(true);
      const response = await adminAPI.addUser(newUserEmail.trim());
      
      if (response.status === 'success') {
        toast.success(response.message);
        setNewUserEmail('');
        fetchUsers();
        fetchStats();
      } else {
        toast.error(response.message);
      }
    } catch (error: any) {
      console.error('Error adding user:', error);
      if (error.response?.status === 403) {
        toast.error('Admin access required');
      } else if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error('Failed to add user');
      }
    } finally {
      setIsAddingUser(false);
    }
  };

  const handleRemoveUser = async (email: string) => {
    if (!window.confirm(`Are you sure you want to remove ${email} from the allowed users list?`)) {
      return;
    }

    try {
      const response = await adminAPI.removeUser(email);
      
      if (response.status === 'success') {
        toast.success(response.message);
        fetchUsers();
        fetchStats();
      } else {
        toast.error(response.message);
      }
    } catch (error: any) {
      console.error('Error removing user:', error);
      if (error.response?.status === 403) {
        toast.error('Admin access required');
      } else if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error('Failed to remove user');
      }
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="bg-white rounded-2xl shadow-xl p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <div className="bg-purple-600 p-2 rounded-lg">
            <Shield className="h-6 w-6 text-white" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Admin Panel</h2>
            <p className="text-sm text-gray-600">Manage user access to the application</p>
          </div>
        </div>
        <button
          onClick={() => { fetchUsers(); fetchStats(); }}
          disabled={isLoading}
          className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:opacity-50"
        >
          <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-blue-50 rounded-lg p-4">
          <div className="flex items-center">
            <Users className="h-8 w-8 text-blue-600" />
            <div className="ml-3">
              <p className="text-sm font-medium text-blue-600">Total Users</p>
              <p className="text-2xl font-bold text-blue-900">{stats.total_users}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-purple-50 rounded-lg p-4">
          <div className="flex items-center">
            <Shield className="h-8 w-8 text-purple-600" />
            <div className="ml-3">
              <p className="text-sm font-medium text-purple-600">Admin Users</p>
              <p className="text-2xl font-bold text-purple-900">{stats.admin_users}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-green-50 rounded-lg p-4">
          <div className="flex items-center">
            <UserPlus className="h-8 w-8 text-green-600" />
            <div className="ml-3">
              <p className="text-sm font-medium text-green-600">Regular Users</p>
              <p className="text-2xl font-bold text-green-900">{stats.regular_users}</p>
            </div>
          </div>
        </div>

        <div className="bg-orange-50 rounded-lg p-4">
          <div className="flex items-center">
            <MessageSquare className="h-8 w-8 text-orange-600" />
            <div className="ml-3">
              <p className="text-sm font-medium text-orange-600">Pending Requests</p>
              <p className="text-2xl font-bold text-orange-900">{stats.pending_requests}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Add User Form */}
      <div className="bg-gray-50 rounded-lg p-4 mb-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
          <UserPlus className="h-5 w-5 mr-2" />
          Add New User
        </h3>
        <form onSubmit={handleAddUser} className="flex space-x-3">
          <div className="flex-1">
            <input
              type="email"
              value={newUserEmail}
              onChange={(e) => setNewUserEmail(e.target.value)}
              placeholder="Enter email address"
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
              disabled={isAddingUser}
            />
          </div>
          <button
            type="submit"
            disabled={isAddingUser || !newUserEmail.trim()}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Plus className="h-4 w-4 mr-2" />
            {isAddingUser ? 'Adding...' : 'Add User'}
          </button>
        </form>
      </div>

      {/* Users List */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <Users className="h-5 w-5 mr-2" />
          Allowed Users ({users.length})
        </h3>
        
        {isLoading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto"></div>
            <p className="text-gray-600 mt-2">Loading users...</p>
          </div>
        ) : users.length === 0 ? (
          <div className="text-center py-8">
            <Users className="h-12 w-12 text-gray-400 mx-auto mb-3" />
            <p className="text-gray-600">No users found</p>
          </div>
        ) : (
          <div className="space-y-3">
            {users.map((user, index) => (
              <div
                key={index}
                className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <div className="flex items-center space-x-3">
                  <div className="bg-gray-300 rounded-full p-2">
                    <Mail className="h-4 w-4 text-gray-600" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">{user.email}</p>
                    <div className="flex items-center space-x-4 text-sm text-gray-500">
                      <span className="flex items-center">
                        <UserPlus className="h-3 w-3 mr-1" />
                        Added by: {user.added_by}
                      </span>
                      <span className="flex items-center">
                        <Calendar className="h-3 w-3 mr-1" />
                        {formatDate(user.added_at)}
                      </span>
                    </div>
                  </div>
                </div>
                
                <div className="flex items-center space-x-2">
                  {user.email === currentUser?.email && (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      You
                    </span>
                  )}
                  <button
                    onClick={() => handleRemoveUser(user.email)}
                    disabled={user.email === currentUser?.email}
                    className="inline-flex items-center p-2 border border-transparent rounded-md text-red-600 hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    title={user.email === currentUser?.email ? "You cannot remove yourself" : "Remove user"}
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Admin Info */}
      <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
        <div className="flex">
          <Shield className="h-5 w-5 text-yellow-600 mt-0.5" />
          <div className="ml-3">
            <h4 className="text-sm font-medium text-yellow-800">Admin Privileges</h4>
            <p className="text-sm text-yellow-700 mt-1">
              As an admin, you can add and remove users from the allowed list. 
              Admin users cannot be removed through this interface for security reasons.
              {stats.pending_requests > 0 && (
                <span className="block mt-1 font-medium">
                  You have {stats.pending_requests} pending access request{stats.pending_requests > 1 ? 's' : ''} to review.
                </span>
              )}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}; 